import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { money } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { TID } from "@/constants/testIds";
import { QRCodeSVG } from "qrcode.react";
import { MagnifyingGlass, CaretLeft, CheckCircle, Receipt, PiggyBank, HandCoins, ArrowRight, QrCode, Money, X } from "@phosphor-icons/react";

/**
 * Mobile-first /agent/collect flow:
 *  1. Pick customer (searchable list, big rows)
 *  2. Pick account (savings deposit or loan repayment)
 *  3. Enter amount (giant input) + confirm
 *  4. Success receipt
 */
export default function AgentCollectPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [customer, setCustomer] = useState(null);
  const [savings, setSavings] = useState([]);
  const [loans, setLoans] = useState([]);
  const [pick, setPick] = useState(null); // {kind, account}
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [method, setMethod] = useState("cash"); // cash | card | upi | neft | rtgs
  const [refNo, setRefNo] = useState("");
  const [denom, setDenom] = useState({ "500": 0, "200": 0, "100": 0, "50": 0, "20": 0, "10": 0, "5": 0 });
  const [showDenom, setShowDenom] = useState(false);
  const [pendingUpi, setPendingUpi] = useState(null); // {id, upi_uri, receipt_no, ...}
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [receipt, setReceipt] = useState(null);

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!customer) return;
    (async () => {
      const [s, l] = await Promise.all([
        api.get(`/savings?customer_id=${customer.id}`),
        api.get(`/loans?customer_id=${customer.id}`),
      ]);
      setSavings(s.data);
      setLoans(l.data.filter((x) => x.status !== "closed"));
    })();
  }, [customer]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return customers;
    return customers.filter(
      (c) => c.name.toLowerCase().includes(s) || (c.phone || "").includes(s)
    );
  }, [customers, q]);

  const numpadPress = (v) => {
    setErr("");
    if (v === "back") return setAmount((a) => a.slice(0, -1));
    if (v === "." && amount.includes(".")) return;
    if (amount.length >= 9) return;
    setAmount((a) => (a === "0" && v !== "." ? v : a + v));
  };

  const submit = async () => {
    if (!pick || !amount || Number(amount) <= 0) {
      setErr("Enter a valid amount"); return;
    }
    setSaving(true); setErr("");
    try {
      if (method === "upi") {
        const { data } = await api.post("/collections/upi/init", {
          customer_id: customer.id,
          kind: pick.kind,
          account_id: pick.account.id,
          amount: Number(amount),
          note: note || null,
        });
        setPendingUpi(data);
      } else {
        const body = {
          customer_id: customer.id,
          kind: pick.kind,
          account_id: pick.account.id,
          amount: Number(amount),
          note: note || null,
          payment_method: method,
        };
        if (method === "cash") {
          Object.entries(denom).forEach(([k, v]) => { body[`denom_${k}`] = Number(v) || 0; });
        } else if (refNo) {
          body.reference_no = refNo;
        }
        const { data } = await api.post("/collections", body);
        setReceipt(data);
        setStep(4);
      }
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to record");
    } finally {
      setSaving(false);
    }
  };

  const confirmUpi = async () => {
    if (!pendingUpi) return;
    setSaving(true); setErr("");
    try {
      const { data } = await api.post(`/collections/upi/${pendingUpi.id}/confirm`);
      setReceipt(data);
      setPendingUpi(null);
      setStep(4);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to confirm");
    } finally {
      setSaving(false);
    }
  };

  const cancelUpi = async () => {
    if (!pendingUpi) return;
    try {
      await api.post(`/collections/upi/${pendingUpi.id}/cancel`);
    } catch { /* ignore */ }
    setPendingUpi(null);
    setExpired(false);
  };

  const [expired, setExpired] = useState(false);
  const [remaining, setRemaining] = useState(0);

  useEffect(() => {
    if (!pendingUpi?.expires_at) return;
    const tick = () => {
      const ms = new Date(pendingUpi.expires_at).getTime() - Date.now();
      const secs = Math.max(0, Math.floor(ms / 1000));
      setRemaining(secs);
      if (secs === 0 && !expired) {
        setExpired(true);
        // Best-effort: tell backend to cancel; sweep will also do it.
        api.post(`/collections/upi/${pendingUpi.id}/cancel`).catch(() => {});
      }
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingUpi?.id]);

  const reset = () => {
    setStep(1); setCustomer(null); setPick(null); setAmount(""); setNote(""); setReceipt(null); setErr(""); setMethod("cash"); setPendingUpi(null);
  };

  // Root layout — mobile-first, full height, brand header
  return (
    <div className="min-h-screen bg-slate-50 pb-32">
      <div className="brand-gradient text-white px-4 pt-6 pb-8 relative">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => (step === 1 ? nav("/dashboard") : (step === 4 ? reset() : setStep(step - 1)))}
              className="w-10 h-10 rounded-full bg-white/15 backdrop-blur flex items-center justify-center"
              data-testid="collect-back-btn"
            >
              <CaretLeft size={18} weight="bold" />
            </button>
            <div className="text-[11px] uppercase tracking-widest text-white/70 font-semibold">
              Step {step === 4 ? "✓" : `${step} of 3`}
            </div>
            <div className="w-10" />
          </div>
          <div className="heading text-2xl font-semibold">
            {step === 1 && "Pick a customer"}
            {step === 2 && "Choose account"}
            {step === 3 && "Enter amount"}
            {step === 4 && "Collection recorded"}
          </div>
          <div className="text-sm text-white/70 mt-1">
            {step === 1 && `Signed in as ${user.name}`}
            {step === 2 && customer?.name}
            {step === 3 && (pick?.kind === "savings_deposit" ? `${productLabel(pick.account.product)} · ${customer.name}` : `Loan repayment · ${customer.name}`)}
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 -mt-4">
        {/* STEP 1 */}
        {step === 1 && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4">
            <div className="relative mb-3">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid={TID.collectCustomerSelect}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name or phone"
                className="w-full pl-10 pr-3 py-3 text-base border border-slate-200 rounded-xl focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
              />
            </div>
            <div className="divide-y divide-slate-100 max-h-[60vh] overflow-y-auto">
              {filtered.length === 0 && (
                <div className="text-sm text-slate-500 py-6 text-center">No customers found.</div>
              )}
              {filtered.map((c) => (
                <button
                  key={c.id}
                  data-testid={`collect-cust-${c.id}`}
                  onClick={() => { setCustomer(c); setStep(2); }}
                  className="w-full flex items-center gap-3 py-3 px-1 hover:bg-slate-50 active:bg-slate-100 rounded-lg transition"
                >
                  <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center heading font-semibold text-lg">
                    {c.name[0]?.toUpperCase()}
                  </div>
                  <div className="flex-1 text-left">
                    <div className="font-medium text-slate-900">{c.name}</div>
                    <div className="text-xs mono text-slate-500">{c.phone}</div>
                  </div>
                  <ArrowRight size={18} className="text-slate-300" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* STEP 2 */}
        {step === 2 && customer && (
          <div className="space-y-3">
            {savings.length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold px-2 mb-2 pt-2">Savings — deposit</div>
                <div className="space-y-2">
                  {savings.map((s) => (
                    <button
                      key={s.id}
                      data-testid={`collect-savings-${s.id}`}
                      onClick={() => { setPick({ kind: "savings_deposit", account: s }); setStep(3); }}
                      className="w-full bg-white border border-slate-200 rounded-2xl p-4 flex items-center gap-3 hover:border-emerald-400 active:scale-[0.99] transition"
                    >
                      <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center">
                        <PiggyBank size={22} weight="fill" />
                      </div>
                      <div className="flex-1 text-left">
                        <div className="font-semibold text-slate-900">{productLabel(s.product)}</div>
                        <div className="text-xs text-slate-500 mono">Bal {money(s.balance)}</div>
                      </div>
                      <ArrowRight size={18} className="text-slate-300" />
                    </button>
                  ))}
                </div>
              </div>
            )}
            {loans.length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold px-2 mb-2 pt-2">Loans — daily collection</div>
                <div className="space-y-2">
                  {loans.map((l) => {
                    const paidPct = l.principal ? Math.max(0, Math.min(100, Math.round(((l.principal - l.outstanding) / l.principal) * 100))) : 0;
                    return (
                    <button
                      key={l.id}
                      data-testid={`collect-loan-${l.id}`}
                      onClick={() => { setPick({ kind: "loan_repayment", account: l }); setAmount(String(l.emi)); setStep(3); }}
                      className="w-full bg-white border border-slate-200 rounded-2xl p-4 hover:border-emerald-400 active:scale-[0.99] transition"
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-11 h-11 rounded-xl bg-amber-50 text-amber-700 flex items-center justify-center">
                          <HandCoins size={22} weight="fill" />
                        </div>
                        <div className="flex-1 text-left">
                          <div className="font-semibold text-slate-900 capitalize">{loanTypeLabel(l.loan_type)}</div>
                          <div className="text-xs text-slate-500 mono">
                            {l.loan_no ? `${l.loan_no} · ` : ""}EMI {money(l.emi)} · Out {money(l.outstanding)}
                          </div>
                          {l.linked_savings_id && (
                            <div className="text-[10px] text-sky-700 font-semibold mt-0.5">
                              Linked to deposit — remainder auto-adjusts at maturity
                            </div>
                          )}
                        </div>
                        <ArrowRight size={18} className="text-slate-300" />
                      </div>
                      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full bg-emerald-500" style={{ width: `${paidPct}%` }} />
                      </div>
                      <div className="text-[10px] text-slate-400 mono mt-1 text-right">{paidPct}% repaid</div>
                    </button>
                  );})}
                </div>
              </div>
            )}
            {savings.length === 0 && loans.length === 0 && (
              <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center text-sm text-slate-500">
                No active accounts. Ask the branch to open a savings or loan first.
              </div>
            )}
          </div>
        )}

        {/* STEP 3 */}
        {step === 3 && pick && !pendingUpi && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
            <div className="flex gap-1.5 mb-4 overflow-x-auto -mx-1 px-1">
              {[
                ["cash", "Cash", "border-emerald-500 bg-emerald-50 text-emerald-800"],
                ["card", "Card", "border-sky-500 bg-sky-50 text-sky-800"],
                ["upi", "UPI QR", "border-sky-500 bg-sky-50 text-sky-800"],
                ["neft", "NEFT", "border-slate-500 bg-slate-100 text-slate-800"],
                ["rtgs", "RTGS", "border-slate-500 bg-slate-100 text-slate-800"],
              ].map(([v, l, activeCls]) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setMethod(v)}
                  data-testid={`method-${v}`}
                  className={`shrink-0 px-3 py-2 rounded-xl border font-semibold text-xs transition-colors ${
                    method === v
                      ? activeCls
                      : "border-slate-200 bg-white text-slate-700"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
            <div className="text-center py-4">
              <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold mb-3">
                Enter amount (INR)
              </div>
              <div
                data-testid={TID.collectAmount}
                className="mono text-emerald-700 font-medium text-6xl min-h-[72px] flex items-center justify-center"
              >
                {amount ? `₹${amount}` : <span className="text-slate-200">₹0</span>}
              </div>
              {pick.kind === "loan_repayment" && (
                <div className="text-xs text-slate-500 mt-2">
                  Suggested EMI: <span className="mono">{money(pick.account.emi)}</span>
                </div>
              )}
              {err && <div className="text-sm text-rose-600 mt-3">{err}</div>}
            </div>
            <div className="grid grid-cols-3 gap-2 mb-3">
              {["1","2","3","4","5","6","7","8","9",".","0","back"].map((k) => (
                <button
                  key={k}
                  onClick={() => numpadPress(k)}
                  data-testid={`numpad-${k}`}
                  className="bg-slate-50 hover:bg-slate-100 active:bg-slate-200 text-slate-900 text-2xl font-medium rounded-2xl h-16 flex items-center justify-center transition-colors mono"
                >
                  {k === "back" ? "⌫" : k}
                </button>
              ))}
            </div>

            {/* Cash denomination panel */}
            {method === "cash" && (
              <div className="mb-3 border border-slate-200 rounded-xl overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowDenom((s) => !s)}
                  className="w-full flex items-center justify-between px-3 py-2.5 bg-slate-50 hover:bg-slate-100 text-sm font-medium text-slate-700"
                  data-testid="toggle-denom"
                >
                  <span className="flex items-center gap-2">
                    <Money size={15} weight="bold" /> Cash denomination
                    {(() => {
                      const t = Object.entries(denom).reduce((s, [k, v]) => s + Number(v || 0) * Number(k), 0);
                      return t > 0 ? <span className="mono text-emerald-700">₹{t}</span> : null;
                    })()}
                  </span>
                  <span className="text-slate-400">{showDenom ? "▲" : "▼"}</span>
                </button>
                {showDenom && (
                  <div className="p-3 space-y-2">
                    {["500", "200", "100", "50", "20", "10", "5"].map((d) => {
                      const count = Number(denom[d]) || 0;
                      const val = count * Number(d);
                      return (
                        <div key={d} className="flex items-center gap-2" data-testid={`denom-row-${d}`}>
                          <div className="w-14 shrink-0 text-sm font-semibold text-slate-700 mono">₹{d}</div>
                          <button
                            type="button"
                            onClick={() => setDenom((prev) => ({ ...prev, [d]: Math.max(0, (Number(prev[d]) || 0) - 1) }))}
                            className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 text-lg font-semibold"
                            data-testid={`denom-${d}-minus`}
                          >−</button>
                          <input
                            type="number"
                            min="0"
                            value={count}
                            onChange={(e) => setDenom((prev) => ({ ...prev, [d]: Math.max(0, Number(e.target.value) || 0) }))}
                            data-testid={`denom-${d}-input`}
                            className="flex-1 min-w-0 text-center px-2 py-1.5 border border-slate-200 rounded-lg text-sm mono outline-none focus:border-emerald-500"
                          />
                          <button
                            type="button"
                            onClick={() => setDenom((prev) => ({ ...prev, [d]: (Number(prev[d]) || 0) + 1 }))}
                            className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 text-lg font-semibold"
                            data-testid={`denom-${d}-plus`}
                          >+</button>
                          <div className="w-16 text-right text-xs mono text-slate-500">{val ? `₹${val}` : ""}</div>
                        </div>
                      );
                    })}
                    {(() => {
                      const t = Object.entries(denom).reduce((s, [k, v]) => s + Number(v || 0) * Number(k), 0);
                      const a = Number(amount) || 0;
                      const diff = t - a;
                      if (!t) return null;
                      return (
                        <div
                          className={`text-xs mono flex items-center justify-between px-1 pt-1 ${
                            diff === 0 ? "text-emerald-700" : "text-amber-700"
                          }`}
                          data-testid="denom-total-line"
                        >
                          <span>Denom total ₹{t}</span>
                          {diff !== 0 && a > 0 && (
                            <span>{diff > 0 ? `+${diff}` : diff} vs amount</span>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}

            {/* Reference number for non-cash non-upi */}
            {(method === "card" || method === "neft" || method === "rtgs") && (
              <input
                value={refNo}
                onChange={(e) => setRefNo(e.target.value)}
                placeholder={`${method.toUpperCase()} reference / txn no.`}
                data-testid="collect-ref-no"
                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-sky-500 mb-3 mono"
              />
            )}

            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Note (optional)"
              data-testid="collect-note"
              className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-500 mb-3"
            />
          </div>
        )}

        {/* UPI PENDING PANEL */}
        {step === 3 && pendingUpi && (
          <div className={`bg-white rounded-2xl border shadow-sm p-5 text-center ${expired ? "border-rose-200" : "border-sky-200"}`} data-testid="upi-pending-panel">
            <div className={`text-[11px] uppercase tracking-widest font-semibold ${expired ? "text-rose-700" : "text-sky-700"}`}>
              {expired ? "QR expired" : "Ask customer to scan"}
            </div>
            <div className={`mono heading text-3xl font-semibold my-2 ${expired ? "text-rose-700 line-through" : "text-sky-800"}`}>
              {money(pendingUpi.amount)}
            </div>
            <div className="text-xs text-slate-500 mb-3 mono">Receipt {pendingUpi.receipt_no}</div>

            {!expired ? (
              <>
                <div className={`inline-block bg-white p-3 rounded-2xl border border-slate-200 ${remaining < 60 ? "ring-2 ring-amber-300" : ""}`}>
                  <QRCodeSVG value={pendingUpi.upi_uri} size={220} level="M" includeMargin={false} data-testid="upi-qr" />
                </div>
                <div className="text-xs text-slate-500 mt-3">
                  Paying to <span className="font-medium text-slate-800">{pendingUpi.upi_payee || "Society"}</span>
                  <br />
                  <span className="mono">{pendingUpi.upi_vpa}</span>
                </div>
                <div
                  className={`mt-3 inline-flex items-center gap-1 text-xs font-medium mono ${
                    remaining < 60 ? "text-amber-700" : "text-slate-500"
                  }`}
                  data-testid="upi-countdown"
                >
                  Expires in {Math.floor(remaining / 60).toString().padStart(2, "0")}:{(remaining % 60).toString().padStart(2, "0")}
                </div>
                <a
                  href={pendingUpi.upi_uri}
                  className="mt-2 block text-xs text-emerald-700 underline"
                  data-testid="upi-open-app"
                >
                  Open UPI app directly
                </a>
              </>
            ) : (
              <div className="py-6" data-testid="upi-expired-view">
                <div className="mx-auto w-16 h-16 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center mb-3">
                  <X size={32} weight="bold" />
                </div>
                <div className="text-sm font-semibold text-slate-900">
                  No payment received in 15 minutes
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  The QR was auto-cancelled. Tap Try again to generate a fresh one.
                </div>
              </div>
            )}

            {err && <div className="text-sm text-rose-600 mt-3">{err}</div>}
          </div>
        )}

        {/* STEP 4 */}
        {step === 4 && receipt && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 text-center fade-up" data-testid={TID.collectReceipt}>
            <div className="mx-auto w-20 h-20 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mb-4">
              <CheckCircle size={44} weight="fill" />
            </div>
            <div className="heading text-2xl font-semibold text-slate-900">Collection recorded</div>
            <div className="mono text-xs text-slate-500 mt-1">{receipt.receipt_no}</div>

            <div className="mono heading text-4xl font-semibold text-emerald-700 my-6">
              {money(receipt.amount)}
            </div>

            <div className="bg-slate-50 rounded-xl p-4 text-left text-sm space-y-2 border border-slate-100">
              <Row k="Customer" v={customer.name} />
              <Row k="Kind" v={receipt.kind === "savings_deposit" ? "Savings deposit" : "Loan repayment"} />
              {"balance_after" in receipt && <Row k="Balance after" v={money(receipt.balance_after)} mono />}
              {"outstanding_after" in receipt && <Row k="Outstanding after" v={money(receipt.outstanding_after)} mono />}
              <Row k="Date" v={new Date(receipt.created_at).toLocaleString()} />
              {receipt.note && <Row k="Note" v={receipt.note} />}
            </div>
          </div>
        )}
      </div>

      {/* Fixed bottom action bar */}
      {step === 3 && !pendingUpi && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-100 p-4 shadow-[0_-4px_20px_rgba(0,0,0,0.05)]">
          <div className="max-w-2xl mx-auto flex gap-2">
            <button
              onClick={() => { setStep(2); setAmount(""); }}
              className="flex-1 h-14 rounded-xl border border-slate-200 text-slate-700 font-semibold"
              data-testid="collect-cancel-btn"
            >
              Back
            </button>
            <button
              onClick={submit}
              disabled={saving || !amount}
              data-testid={TID.collectSubmit}
              className={`flex-[2] h-14 rounded-xl text-white font-semibold text-lg active:scale-[0.98] transition-transform disabled:opacity-50 ${
                method === "upi" ? "bg-sky-600" : "bg-emerald-700"
              }`}
            >
              {saving ? "Working…" : (
                <span className="inline-flex items-center gap-2 justify-center">
                  {method === "upi" ? <><QrCode size={20} weight="bold" /> Generate QR</> : <><Receipt size={20} weight="bold" /> Confirm & receipt</>}
                </span>
              )}
            </button>
          </div>
        </div>
      )}

      {step === 3 && pendingUpi && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-100 p-4 shadow-[0_-4px_20px_rgba(0,0,0,0.05)]">
          <div className="max-w-2xl mx-auto flex gap-2">
            {expired ? (
              <button
                onClick={() => { setPendingUpi(null); setExpired(false); }}
                className="w-full h-14 rounded-xl bg-emerald-700 text-white font-semibold text-lg active:scale-[0.98] transition-transform inline-flex items-center justify-center gap-2"
                data-testid="upi-tryagain-btn"
              >
                <QrCode size={20} weight="bold" /> Try again
              </button>
            ) : (
              <>
                <button
                  onClick={cancelUpi}
                  className="flex-1 h-14 rounded-xl border border-slate-200 text-slate-700 font-semibold inline-flex items-center justify-center gap-1"
                  data-testid="upi-cancel-btn"
                >
                  <X size={16} /> Cancel
                </button>
                <button
                  onClick={confirmUpi}
                  disabled={saving}
                  className="flex-[2] h-14 rounded-xl bg-emerald-700 text-white font-semibold text-lg active:scale-[0.98] transition-transform disabled:opacity-50"
                  data-testid="upi-confirm-btn"
                >
                  {saving ? "Confirming…" : (
                    <span className="inline-flex items-center gap-2 justify-center">
                      <CheckCircle size={20} weight="fill" /> Payment received
                    </span>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-100 p-4">
          <div className="max-w-2xl mx-auto flex gap-2">
            <Link
              to="/collections"
              className="flex-1 h-14 rounded-xl border border-slate-200 text-slate-700 font-semibold flex items-center justify-center"
              data-testid="collect-view-all-btn"
            >
              View all
            </Link>
            <button
              onClick={reset}
              className="flex-[2] h-14 rounded-xl bg-emerald-700 text-white font-semibold text-lg active:scale-[0.98] transition-transform"
              data-testid={TID.collectNewBtn}
            >
              New collection
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ k, v, mono }) {
  return (
    <div className="flex justify-between gap-3">
      <div className="text-slate-500">{k}</div>
      <div className={`text-slate-900 font-medium text-right ${mono ? "mono" : ""}`}>{v}</div>
    </div>
  );
}

const productLabel = (p) => ({ daily: "Daily Savings", rd: "Recurring Deposit", fd: "Fixed Deposit" }[p] || p);
const loanTypeLabel = (t) => ({ deposit: "Deposit-backed loan", general: "General loan", commodity: "Commodity loan" }[t] || t);
